/**
 * `<ToolButton>` — 28×28 square toolbar button. Used in the editor
 * formatting bar, graph zoom controls, etc.
 *
 * For these dense toolbars, ensure `title` is set since icon-only buttons
 * have no visible text (accessibility guideline from the design system).
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

interface ToolButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Active state — applies the `.on` class. */
  active?: boolean;
  children: ReactNode;
  /** Required because the button is icon-only. */
  title: string;
}

export const ToolButton = forwardRef<HTMLButtonElement, ToolButtonProps>(function ToolButton(
  { active = false, children, className = '', title, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`tool${active ? ' on' : ''} ${className}`.trim()}
      title={title}
      aria-label={title}
      {...rest}
    >
      {children}
    </button>
  );
});
